/**
 * Registry of modules supported by the bulk channel module enablement tool.
 *
 * Each entry declares how to read and write a module's enabled state on a
 * channel, without leaking any mutation-specific detail into the UI layer.
 * Add new modules here as they graduate from the pilot programme.
 */

/**
 * All boolean settings fields surfaced by the bulk tool's selection queries.
 *
 * These mirror the scalar Boolean fields on `ChannelSettings` (see
 * `libs/graphql-schemas/src/lib/types/ChannelSettings.graphql`). Complex
 * sub-types (groupRoleJoinRules, requirements) and JSON fields are excluded.
 *
 * When a new module is added to the registry:
 * 1. Add its settings field here (`ChannelModuleSettings`).
 * 2. Add the field to the `BulkModuleSettings` fragment in
 *    `useBulkChannelSelection.ts`.
 * That ensures the field is fetched alongside every channel in the selection
 * queries.
 */
export type ChannelModuleSettings = {
  hasPerksEnabled?: boolean | null;
  hasBillingPaymentsEnabled?: boolean | null;
  hasWorkOrderServiceRequestsEnabled?: boolean | null;
  hasWorkOrderPreventiveMaintenanceEnabled?: boolean | null;
  hasWorkOrderEquipmentEnabled?: boolean | null;
  hasVisitorManagementEnabled?: boolean | null;
  hasHardwareManagementEnabled?: boolean | null;
  hasHardwarePinManagementEnabled?: boolean | null;
  hasReservableManagementEnabled?: boolean | null;
  hasWorkOrderCrossPropertyEnabled?: boolean | null;
  hasAccessControlEnabled?: boolean | null;
  hasSmartUserManagementEnabled?: boolean | null;
  multiLanguageEnabled?: boolean | null;
  hasSurveysEnabled?: boolean | null;
  hasMessagingAndMarketplaceEnabled?: boolean | null;
  hasInspectionsEnabled?: boolean | null;
};

/** Minimal channel shape required by `isAlreadyEnabled` checks. */
export type ChannelForModuleCheck = {
  _id: string;
  settings?: ChannelModuleSettings | null;
};

export type BulkModuleDefinition = {
  /** Stable identifier used as React list key and for analytics. */
  id: string;
  /** i18n key for the toggle label. */
  labelTitleKey: string;
  /** i18n key for the toggle description. */
  labelDescriptionKey: string;
  /**
   * Returns true if the channel already has this module enabled.
   * Receives a channel with the fields declared in `ChannelModuleSettings`.
   */
  isAlreadyEnabled: (channel: ChannelForModuleCheck) => boolean;
  /**
   * Returns the settings patch that should be merged into the channel update
   * payload to enable this module.
   */
  buildEnablePatch: () => { settings: Record<string, boolean> };
};

export const inspectionsModule: BulkModuleDefinition = {
  id: 'inspections',
  labelTitleKey: 'web.content.feature.inspections.toggleFeature.title',
  labelDescriptionKey:
    'web.content.feature.inspections.toggleFeature.description',
  isAlreadyEnabled: channel =>
    channel.settings?.hasInspectionsEnabled === true,
  buildEnablePatch: () => ({ settings: { hasInspectionsEnabled: true } }),
};

/** All modules available for bulk enablement. Inspections is first. */
export const BULK_MODULE_REGISTRY: BulkModuleDefinition[] = [
  inspectionsModule,
];
